import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import MemberCard from './MemberCard';
import { getAllMembers } from '../utils/memberLoader';
import { staggerContainer, cardVariant, fadeInUp } from '../utils/animations';

function Members() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        const allMembers = await getAllMembers();
        setMembers(allMembers);
        setLoading(false);
      } catch (error) {
        console.error('Error loading members:', error);
        setLoading(false);
      }
    };

    loadMembers();
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Community Members - Red Panda Treehouse</title>
        <meta name="description" content="Meet the extraordinary talents that make up the Red Panda Treehouse community. Discover diverse red panda VTubers from our inclusive, LGBTQ+ friendly community." />
        <meta name="keywords" content="red panda vtubers, vtuber community members, content creators, streaming community, LGBTQ+ vtubers" />
        <link rel="canonical" href="https://redpandatreehouse.com/members" />

        {/* Open Graph */}
        <meta property="og:title" content="Our Community Members - Red Panda Treehouse" />
        <meta property="og:description" content="Meet the extraordinary talents that make up the Red Panda Treehouse community. Discover diverse red panda VTubers from our inclusive, LGBTQ+ friendly community." />
        <meta property="og:url" content="https://redpandatreehouse.com/members" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Our Community Members - Red Panda Treehouse" />
        <meta name="twitter:description" content="Meet the extraordinary talents that make up the Red Panda Treehouse community." />
      </Helmet>
      <section className='relative bg-gradient-to-b from-primary-dark to-secondary-dark pt-24 opacity-80'>
        <div className='container mx-auto px-4 text-center'>
          <motion.h1 
            className='text-5xl md:text-6xl font-bold mb-4'
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Our Community
          </motion.h1>
          <motion.p 
            className='text-xl opacity-90 max-w-3xl mx-auto'
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Meet the extraordinary talents that make up the Red Panda Treehouse
            community
          </motion.p>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-4'>
          {loading ? (
            <div className='text-center w-full py-10'>
              <div className='inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-DEFAULT'></div>
              <p className='mt-4 text-lg'>Loading members...</p>
            </div>
          ) : members.length === 0 ? (
            <div className='text-center py-10'>
              <p className='text-2xl'>No members found</p>
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {members.map((member, index) => (
                <motion.div 
                  key={member.id} 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={cardVariant}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                >
                  <MemberCard member={member} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Members;
